import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  TableCell,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  LinearProgress,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ACCOUNT_DETAIL_FONT_FAMILY, ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";

export const CELL_TEXT = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

export const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

export const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

export const SEARCH_FIELD_SX = {
  flex: { xs: "1 1 160px", sm: "0 0 auto" },
  width: { xs: "auto", sm: 220 },
  minWidth: { xs: 0, sm: 160 },
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

export const SELECT_SX = {
  height: 40,
  minWidth: { xs: 0, sm: 150 },
  width: { xs: "100%", sm: "auto" },
  fontSize: "0.875rem",
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e2e8f0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#cbd5e1",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    color: "#94a3b8",
  },
};

export const MENU_PROPS = {
  PaperProps: {
    sx: {
      fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
      border: "1px solid #e2e8f0",
      mt: 0.5,
      maxHeight: 320,
      "& .MuiMenuItem-root": {
        fontFamily: "inherit",
        fontSize: "0.875rem",
        fontWeight: 500,
      },
    },
  },
};

export const CHECKBOX_SX = {
  p: 0.5,
  color: "#cbd5e1",
  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
    color: "#F98125",
  },
};

export const PROGRESS_SX = {
  height: 2,
  backgroundColor: "#ffedd5",
  "& .MuiLinearProgress-bar": { backgroundColor: "#F98125" },
};

export function ConfigCard({ children }) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      {children}
    </Box>
  );
}

export function LoadingState({ label }) {
  return (
    <Box
      aria-live="polite"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        py: 8,
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        backgroundColor: "#fff",
      }}
    >
      <CircularProgress size={32} sx={{ color: "#64748b" }} />
      <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, ml: 2, color: "#64748b", fontSize: "0.875rem", fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}

export function ErrorBanner({ message, onRetry }) {
  if (!message) return null;
  return (
    <Box
      role="alert"
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: "#fef2f2",
        borderRadius: "8px",
        border: "1px solid #fecaca",
        display: "flex",
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: 1.5,
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#991b1b", fontSize: "0.875rem", fontWeight: 500 }}>
        {message}
      </Typography>
      {onRetry ? (
        <Button
          onClick={onRetry}
          variant="outlined"
          sx={{
            fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
            alignSelf: { xs: "flex-end", sm: "center" },
            textTransform: "none",
            color: "#991b1b",
            borderColor: "#fecaca",
            borderRadius: "8px",
            fontWeight: 600,
            height: 36,
            flexShrink: 0,
            "&:hover": { borderColor: "#fca5a5", backgroundColor: "#fff" },
          }}
        >
          Retry
        </Button>
      ) : null}
    </Box>
  );
}

export function TableFrame({ refreshing, children, footer }) {
  return (
    <Box
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        backgroundColor: "#fff",
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      {refreshing ? <LinearProgress sx={PROGRESS_SX} /> : null}
      {children}
      {footer}
    </Box>
  );
}

export function EmptyRow({ colSpan, label }) {
  return (
    <TableCell colSpan={colSpan} align="center" sx={{ ...BODY_CELL_SX, py: 6, borderBottom: "none" }}>
      <Typography sx={{ ...CELL_TEXT, color: "#64748b" }}>{label}</Typography>
    </TableCell>
  );
}

export function ListFooter({
  page,
  pageSize,
  total,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  disabled = false,
}) {
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = total === 0 ? 0 : Math.min(page * pageSize, total);
  const atStart = page <= 1 || disabled;
  const atEnd = page >= totalPages || totalPages === 0 || disabled;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        px: 2,
        py: 1,
        borderTop: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        minHeight: 48,
      }}
    >
      <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#64748b", fontSize: "0.75rem" }}>Rows per page</Typography>
      <FormControl size="small">
        <Select
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          disabled={disabled}
          sx={{
            fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
            fontSize: "0.75rem",
            height: 28,
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
          }}
          MenuProps={MENU_PROPS}
        >
          {pageSizeOptions.map((option) => (
            <MenuItem key={option} value={option} sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, fontSize: "0.8rem" }}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography sx={{ fontFamily: ACCOUNT_DETAIL_FONT_FAMILY, color: "#64748b", fontSize: "0.75rem", ml: 0.5 }}>
        {total ? `${start}–${end} of ${total}` : "0 of 0"}
      </Typography>
      <IconButton size="small" aria-label="First page" onClick={() => onPageChange(1)} disabled={atStart} sx={{ color: atStart ? "#d1d5db" : "#64748b" }}>
        <FirstPageIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <IconButton size="small" aria-label="Previous page" onClick={() => onPageChange(Math.max(1, page - 1))} disabled={atStart} sx={{ color: atStart ? "#d1d5db" : "#64748b" }}>
        <ArrowBackIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" aria-label="Next page" onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={atEnd} sx={{ color: atEnd ? "#d1d5db" : "#64748b" }}>
        <ArrowForwardIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" aria-label="Last page" onClick={() => onPageChange(totalPages)} disabled={atEnd} sx={{ color: atEnd ? "#d1d5db" : "#64748b" }}>
        <LastPageIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}
